/**
 * Access token manager compatible with legacy flow
 * - Stores access token in-memory with expiry
 * - Uses refresh token from Cookie (TokenManager) to get new access token
 * - Calls /token/refresh/ to obtain { access }
 */
import axios from "axios";
import { TokenManager } from "./auth";

class AccessTokenManager {
  private accessToken: string | null = null;
  private tokenExpiry: number | null = null;
  private refreshPromise: Promise<string> | null = null;
  private readonly TOKEN_BUFFER_TIME = 60 * 1000; // refresh 1 min early

  private parseJwt(token: string): { exp?: number } | null {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  }

  private isTokenExpired(): boolean {
    if (!this.tokenExpiry) return true;
    return Date.now() >= this.tokenExpiry - this.TOKEN_BUFFER_TIME;
  }

  async getAccessToken(): Promise<string> {
    if (this.accessToken && !this.isTokenExpired()) {
      return this.accessToken;
    }
    if (this.refreshPromise) return this.refreshPromise;

    // 🔧 修复：添加超时保护，防止refresh操作无限期挂起
    this.refreshPromise = Promise.race([
      this.refreshAccessToken(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Token refresh timeout")), 15000)
      )
    ]);

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  async refreshAccessToken(): Promise<string> {
    const refresh = TokenManager.getToken(); // refresh token stored in cookie (already decoded)
    if (!refresh) {
      this.clear();
      throw new Error("No refresh token available");
    }
    try {
      const res = await axios.post(
        "/api/token/refresh/",
        { refresh },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 10000  // 🔧 修复：添加10秒超时配置，防止无限等待
        },
      );
      const data = res.data || res;
      const access = data?.access;
      if (!access) throw new Error("Invalid token refresh response");

      this.accessToken = access;
      const payload = this.parseJwt(access);
      if (payload?.exp) {
        this.tokenExpiry = payload.exp * 1000;
      } else {
        // fallback 30 minutes
        this.tokenExpiry = Date.now() + 30 * 60 * 1000;
      }
      return access;
    } catch (err) {
      this.clear();
      throw err;
    }
  }

  /**
   * 直接注入 access token（登录拿到 {access,refresh} 时调用），
   * 避免登录后首个请求触发冷启动 refresh 竞态。
   */
  setAccessToken(access: string): void {
    if (!access) return;
    this.accessToken = access;
    const payload = this.parseJwt(access);
    this.tokenExpiry = payload?.exp ? payload.exp * 1000 : Date.now() + 30 * 60 * 1000;
  }

  clear(): void {
    this.accessToken = null;
    this.tokenExpiry = null;
    this.refreshPromise = null;
  }
}

export const accessTokenManager = new AccessTokenManager();
export default accessTokenManager;
